import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  useGtinDumpSyncInbox,
  type TrackedDumpSync,
} from '../hooks/useGtinDumpSyncInbox';

type GtinDumpSyncInboxContextValue = {
  job: TrackedDumpSync | null;
  inFlight: boolean;
  showResult: boolean;
  dismissResult: () => void;
  importProgress: {
    current: number;
    total: number;
    phase: TrackedDumpSync['phase'];
    indeterminate: boolean;
    created: number | null;
    updated: number | null;
  } | null;
  /** Extra inbox badge slots while dump sync progress / result visible. */
  inboxDumpSyncItemCount: number;
};

const Ctx = createContext<GtinDumpSyncInboxContextValue | null>(null);

export function GtinDumpSyncInboxProvider({ children }: { children: ReactNode }) {
  const inbox = useGtinDumpSyncInbox();

  const value = useMemo<GtinDumpSyncInboxContextValue>(() => {
    const inboxDumpSyncItemCount =
      (inbox.inFlight ? 1 : 0) + (inbox.showResult && !inbox.inFlight ? 1 : 0);
    return {
      job: inbox.job,
      inFlight: inbox.inFlight,
      showResult: inbox.showResult,
      dismissResult: inbox.dismissResult,
      importProgress: inbox.importProgress,
      inboxDumpSyncItemCount,
    };
  }, [inbox]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGtinDumpSyncInboxContextOptional() {
  return useContext(Ctx);
}

export function useGtinDumpSyncInboxContext() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useGtinDumpSyncInboxContext requires GtinDumpSyncInboxProvider');
  return v;
}
